import { Home, Menu, Search, Settings, UserRound, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CreditsBadge } from '../components/CreditsBadge';
import { NotificationsBell } from '../components/NotificationsBell';
import { WorkspaceSwitcher } from '../components/WorkspaceSwitcher';
import { useAuthStore } from '../lib/auth';
import { getLandingUrl } from '../lib/urls';

interface HeaderProps {
  onMenuClick?: () => void;
  isMenuOpen?: boolean;
}

export function Header({ onMenuClick, isMenuOpen }: HeaderProps) {
  const { t } = useTranslation();
  const { user, clearAuth } = useAuthStore();
  const navigate = useNavigate();
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 flex h-[var(--topbar-height)] shrink-0 items-center gap-3 border-b border-border/80 bg-background/80 px-4 backdrop-blur-xl sm:px-6 lg:px-8">
      <button
        type="button"
        onClick={onMenuClick}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-muted-foreground transition hover:bg-accent hover:text-foreground lg:hidden"
        aria-expanded={isMenuOpen}
      >
        <span className="sr-only">{t('Abrir menú')}</span>
        {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
        <div className="relative hidden max-w-xl flex-1 md:block">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <input
            className="h-10 w-full rounded-xl border border-border bg-card/60 py-2 pl-10 pr-16 text-sm text-foreground shadow-sm transition placeholder:text-muted-foreground hover:border-primary/30 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
            placeholder={t('Buscar...')}
            type="search"
            name="search"
          />
          <kbd className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded-md border border-border bg-muted/70 px-1.5 py-0.5 text-[0.65rem] font-semibold text-muted-foreground">
            Ctrl K
          </kbd>
        </div>

        <div className="ml-auto flex min-w-0 items-center gap-1.5 sm:gap-2.5">
          <WorkspaceSwitcher />
          <CreditsBadge />
          <NotificationsBell />

          <div className="relative ml-1 border-l border-border pl-2 sm:ml-2 sm:pl-3">
            <button
              type="button"
              className="flex min-w-0 items-center gap-2 rounded-xl p-1.5 text-left transition hover:bg-accent"
              onClick={() => setIsProfileOpen(open => !open)}
              aria-expanded={isProfileOpen}
              aria-haspopup="menu"
            >
              <span className="sr-only">{t('Abrir menú de usuario')}</span>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/15 text-primary">
                <UserRound className="h-[1.1rem] w-[1.1rem]" aria-hidden="true" />
              </span>
              <span className="hidden min-w-0 xl:block">
                <span className="block max-w-36 truncate text-sm font-semibold text-foreground">
                  {user?.name || user?.email}
                </span>
                <span className="block text-xs text-muted-foreground">{t('Mi cuenta')}</span>
              </span>
            </button>

            {isProfileOpen && (
              <>
                <button
                  className="fixed inset-0 z-10 cursor-default"
                  onClick={() => setIsProfileOpen(false)}
                  aria-label={t('Cerrar menú de usuario')}
                />
                <div className="absolute right-0 z-20 mt-2.5 w-56 overflow-hidden rounded-2xl border border-border bg-popover p-2 text-popover-foreground shadow-2xl" role="menu">
                  <div className="border-b border-border px-3 py-2.5 xl:hidden">
                    <p className="truncate text-sm font-semibold">{user?.name || user?.email}</p>
                    <p className="text-xs text-muted-foreground">{t('Mi cuenta')}</p>
                  </div>
                  <a
                    href={getLandingUrl()}
                    className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-muted-foreground transition hover:bg-accent hover:text-foreground"
                    onClick={() => setIsProfileOpen(false)}
                    role="menuitem"
                  >
                    <Home className="h-4 w-4" aria-hidden="true" />
                    {t('Página principal')}
                  </a>
                  <button
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-muted-foreground transition hover:bg-accent hover:text-foreground"
                    onClick={() => {
                      navigate('/settings');
                      setIsProfileOpen(false);
                    }}
                    role="menuitem"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                    {t('Configuración')}
                  </button>
                  <div className="my-1 border-t border-border" />
                  <button
                    className="w-full rounded-xl px-3 py-2.5 text-left text-sm font-medium text-danger transition hover:bg-danger/10"
                    onClick={() => {
                      clearAuth();
                      setIsProfileOpen(false);
                    }}
                    role="menuitem"
                  >
                    {t('Cerrar sesión')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
